import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Tv, Users, Radio, AlertCircle, Calendar, Clock, MapPin, CalendarDays } from 'lucide-react';
import { turkeyCities } from '../data/turkeyCities';

const getTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateLabel = (value) => {
    if (!value) return '';
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });
};

const toMinutes = (hhmm) => {
    const [h, m] = (hhmm || '').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

// entry.time "20:00 - 21:00" gibi bir aralık; seçilen saat bu aralığın içine düşüyor mu?
const isTimeWithinSlot = (slotStr, hhmm) => {
    if (!slotStr || !hhmm) return false;
    const parts = slotStr.split('-').map(s => s.trim());
    if (parts.length !== 2) return slotStr.trim() === hhmm;
    const start = toMinutes(parts[0]);
    let end = toMinutes(parts[1]);
    if (end <= start) end += 24 * 60;
    const t = toMinutes(hhmm);
    return t >= start && t < end;
};

const LiveStream = () => {
    const [fields, setFields] = useState([]);
    const [allBookings, setAllBookings] = useState([]);
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [scheduleDate, setScheduleDate] = useState(getTodayStr());
    const [scheduleTime, setScheduleTime] = useState("");
    const [scheduleFieldId, setScheduleFieldId] = useState("");
    const [selectedField, setSelectedField] = useState(null);
    const [hlsLoaded, setHlsLoaded] = useState(false);
    const [playbackError, setPlaybackError] = useState(false);
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const todayStr = getTodayStr();

    // 1. Hls.js CDN yüklemesi
    useEffect(() => {
        const scriptId = 'hls-cdn-script';
        let script = document.getElementById(scriptId);

        if (!script) {
            script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
            script.async = true;
            script.onload = () => setHlsLoaded(true);
            document.body.appendChild(script);
        } else {
            setHlsLoaded(true);
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
        };
    }, []);

    // 2. Onaylı sahaları Firestore'dan çek (liveStreamUrl olanları önce göster)
    useEffect(() => {
        const q = query(collection(db, "tesisler"), where("status", "==", "approved"));
        const unsub = onSnapshot(q, (snapshot) => {
            const dbFields = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    fieldName: data.fieldName || data.name || "Bilinmeyen Saha",
                    city: data.city || "",
                    district: data.district || "",
                    liveStreamUrl: data.liveStreamUrl || "",
                    isLive: !!data.liveStreamUrl,
                    viewerCount: data.liveStreamUrl ? Math.floor(Math.random() * 30) + 5 : 0
                };
            });

            // Canlı olanları üste al
            const sorted = [...dbFields].sort((a, b) => (b.isLive ? 1 : 0) - (a.isLive ? 1 : 0));
            setFields(sorted);

            if (sorted.length > 0 && !selectedField) {
                const liveOne = sorted.find(f => f.isLive);
                setSelectedField(liveOne || sorted[0]);
            }
        });

        return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 3. Maç programı için rezervasyonları çek
    useEffect(() => {
        const unsub = onSnapshot(collection(db, "rezervasyonlar"), (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllBookings(data);
        });
        return () => unsub();
    }, []);

    const filteredFields = fields.filter(field => {
        const cityMatch = selectedCity === "" || (field.city || "").toLowerCase() === selectedCity.toLowerCase() || (field.city === "Yerel Ağ");
        const districtMatch = selectedDistrict === "" || (field.district || "").toLowerCase() === selectedDistrict.toLowerCase() || (field.district === "Ev/Saha");
        return cityMatch && districtMatch;
    });

    // Halı Saha filtresi için, seçili şehir/ilçeye göre tesis listesi
    const fieldOptionsForSchedule = fields.filter(field => {
        const cityMatch = selectedCity === "" || (field.city || "").toLowerCase() === selectedCity.toLowerCase();
        const districtMatch = selectedDistrict === "" || (field.district || "").toLowerCase() === selectedDistrict.toLowerCase();
        return cityMatch && districtMatch;
    });

    // Maç programı: rezervasyonları sahalarla eşleştir, filtrele ve sırala
    const scheduleEntries = allBookings
        .filter(b => b.status === "confirmed" || b.status === "manuel_booked")
        .map(b => {
            const field = fields.find(f => f.id === b.fieldId);
            if (!field) return null;
            return {
                id: b.id,
                fieldId: b.fieldId,
                fieldName: b.fieldName || field.fieldName,
                city: field.city,
                district: field.district,
                date: b.date,
                time: b.timeSlot || b.time || "",
                isLive: field.isLive
            };
        })
        .filter(Boolean)
        .filter(e => selectedCity === "" || (e.city || "").toLowerCase() === selectedCity.toLowerCase())
        .filter(e => selectedDistrict === "" || (e.district || "").toLowerCase() === selectedDistrict.toLowerCase())
        .filter(e => scheduleDate === "" || e.date === scheduleDate)
        .filter(e => scheduleTime === "" || isTimeWithinSlot(e.time, scheduleTime))
        .filter(e => scheduleFieldId === "" || e.fieldId === scheduleFieldId)
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

    const todaysMatchCount = allBookings.filter(b =>
        (b.status === "confirmed" || b.status === "manuel_booked") && b.date === todayStr
    ).length;
    const liveCount = fields.filter(f => f.isLive).length;

    // 4. Seçili Saha Değiştiğinde Yayını Başlat
    useEffect(() => {
        if (!selectedField || !videoRef.current) return;
        setPlaybackError(false);

        const video = videoRef.current;
        const streamUrl = selectedField.liveStreamUrl;

        // Eski Hls örneğini temizle
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari ve iOS desteği (Native HLS)
            video.src = streamUrl;
            video.addEventListener('loadedmetadata', () => {
                video.play().catch(e => console.log("Oynatma hatası:", e));
            });
        } else if (window.Hls) {
            // Diğer tarayıcılar için Hls.js kullanımı
            const hls = new window.Hls({
                maxMaxBufferLength: 10,
                enableWorker: true,
                lowLatencyMode: true
            });
            hlsRef.current = hls;

            hls.loadSource(streamUrl);
            hls.attachMedia(video);

            hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(e => console.log("Oynatma hatası:", e));
            });

            hls.on(window.Hls.Events.ERROR, (event, data) => {
                console.warn("HLS Oynatıcısı Hatası:", data);
                if (data.fatal) {
                    switch (data.type) {
                        case window.Hls.ErrorTypes.NETWORK_ERROR:
                            hls.startLoad();
                            break;
                        case window.Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            setPlaybackError(true);
                            hls.destroy();
                            break;
                    }
                }
            });
        } else {
            setPlaybackError(true);
        }
    }, [selectedField, hlsLoaded]);

    return (
        <div className="min-vh-100 bg-light py-5">
            <style>
                {`
                    .archive-card {
                        background: #ffffff;
                        border-radius: 20px;
                        overflow: hidden;
                        box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                        border: 1px solid #f1f5f9;
                    }
                    .archive-item {
                        border-radius: 12px;
                        transition: all 0.2s ease;
                        cursor: pointer;
                    }
                    .archive-item:hover {
                        background: #f8fafc;
                        transform: translateX(4px);
                    }
                    .archive-item.active {
                        background: #e2e8f0;
                        border-left: 4px solid #0f172a;
                    }
                    .video-wrapper {
                        background: #000;
                        border-radius: 16px;
                        overflow: hidden;
                        position: relative;
                        padding-top: 56.25%;
                    }
                    .video-wrapper video {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        object-fit: contain;
                    }
                    .pulse-icon {
                        animation: pulse-animation 1.5s infinite;
                    }
                    @keyframes pulse-animation {
                        0% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.4; transform: scale(0.9); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                `}
            </style>

            <div className="container">
                {/* Sayfa Başlığı */}
                <div className="mb-5 text-center">
                    <h1 className="fw-bold text-dark d-flex align-items-center justify-content-center gap-2">
                        <Radio className="pulse-icon text-success" size={32} /> Canlı Yayın İzle
                    </h1>
                    <p className="text-muted mb-3">Halı sahalardaki maçları kesintisiz ve yüksek kalitede canlı izleyin.</p>
                    <div className="d-flex flex-wrap justify-content-center gap-2">
                        <span className="badge bg-light text-dark border small py-2 px-3 rounded-pill d-flex align-items-center gap-1">
                            <Users size={14} /> Toplam İzleyici: {filteredFields.reduce((acc, f) => acc + (f.viewerCount || 0), 0)}
                        </span>
                        <span className="badge bg-light text-dark border small py-2 px-3 rounded-pill d-flex align-items-center gap-1">
                            <Radio size={14} className="text-success" /> Aktif Yayın: {liveCount}
                        </span>
                        <span className="badge bg-light text-dark border small py-2 px-3 rounded-pill d-flex align-items-center gap-1">
                            <CalendarDays size={14} className="text-warning" /> Bugün {todaysMatchCount} Maç
                        </span>
                    </div>
                </div>

                {/* Filtreleme Kartı */}
                <div className="card border-0 shadow-sm p-4 mb-5 rounded-4 bg-white">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Şehir</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={selectedCity} onChange={(e) => { setSelectedCity(e.target.value); setSelectedDistrict(""); setScheduleFieldId(""); }}>
                                <option value="">Tüm Türkiye</option>
                                {Object.keys(turkeyCities).sort().map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold small mb-1 ms-1 text-muted">İlçe</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={selectedDistrict} onChange={(e) => { setSelectedDistrict(e.target.value); setScheduleFieldId(""); }} disabled={!selectedCity}>
                                <option value="">Tümü</option>
                                {selectedCity && turkeyCities[selectedCity].sort().map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Halı Saha</label>
                            <select className="form-select border-0 bg-light p-3 shadow-none rounded-3" value={scheduleFieldId} onChange={(e) => setScheduleFieldId(e.target.value)}>
                                <option value="">Tüm Sahalar</option>
                                {fieldOptionsForSchedule.map(f => <option key={f.id} value={f.id}>{f.fieldName}</option>)}
                            </select>
                        </div>
                        <div className="col-md-5">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Tarih (Maç Programı)</label>
                            <input type="date" className="form-control border-0 bg-light p-3 shadow-none rounded-3" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
                        </div>
                        <div className="col-md-5">
                            <label className="fw-bold small mb-1 ms-1 text-muted">Saat (Maç Programı)</label>
                            <input type="time" className="form-control border-0 bg-light p-3 shadow-none rounded-3" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100 p-3 rounded-3 fw-bold"
                                onClick={() => { setScheduleDate(""); setScheduleTime(""); }}
                            >
                                Tümünü Gör
                            </button>
                        </div>
                    </div>
                </div>

                {/* Ana İçerik: Oynatıcı + Aktif Sahalar */}
                <div className="row g-4 mb-4">
                    {/* SOL TARAF: VİDEO PLAYER */}
                    <div className="col-lg-8">
                        <div className="archive-card p-4">
                            <div className="video-wrapper mb-4 position-relative">
                                {playbackError || !selectedField?.isLive || !selectedField?.liveStreamUrl ? (
                                    <div className="position-absolute top-50 start-50 translate-middle text-center p-3 w-100">
                                        <AlertCircle size={48} className="text-warning mb-3 mx-auto" />
                                        <h5 className="fw-bold text-white">Yayın Akışı Çevrimdışı</h5>
                                        <p className="text-white-50 small px-4">Bu sahada şu an aktif yayın bulunmamaktadır. Tesis sahibi henüz bir canlı yayın kamerası bağlamadı.</p>
                                    </div>
                                ) : (
                                    <video
                                        ref={videoRef}
                                        controls
                                        autoPlay
                                        muted
                                        playsInline
                                    />
                                )}
                                {selectedField?.isLive && selectedField?.liveStreamUrl && (
                                    <div className="position-absolute top-0 start-0 m-3 bg-danger px-3 py-1 rounded-pill d-flex align-items-center gap-1 shadow">
                                        <span className="pulse-icon bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></span>
                                        <span className="fw-bold text-white small" style={{ fontSize: '11px', letterSpacing: '1px' }}>CANLI YAYIN</span>
                                    </div>
                                )}
                            </div>

                            {selectedField && (
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <h4 className="fw-bold text-dark mb-1">{selectedField.fieldName}</h4>
                                        <p className="text-muted mb-0">
                                            <MapPin className="me-1 d-inline-block" size={16} /> {selectedField.city} / {selectedField.district}
                                        </p>
                                    </div>
                                    {selectedField.isLive && (
                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 fs-6 py-2 px-3 rounded-pill d-flex align-items-center gap-1">
                                            <Users size={18} /> {selectedField.viewerCount} İzleyici
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SAĞ TARAF: SAHA LİSTESİ */}
                    <div className="col-lg-4">
                        <div className="archive-card p-4 h-100" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            <h5 className="fw-bold text-dark mb-4"><Tv className="text-success" size={18} /> Aktif Sahalar ({filteredFields.length})</h5>
                            <div className="d-flex flex-column gap-3">
                                {filteredFields.map((field) => (
                                    <div
                                        key={field.id}
                                        onClick={() => setSelectedField(field)}
                                        className={`archive-item p-3 border ${selectedField?.id === field.id ? 'active' : ''}`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="fw-bold mb-1 text-dark">{field.fieldName}</h6>
                                                <small className="text-muted d-flex align-items-center gap-1"><MapPin size={11} /> {field.city} / {field.district}</small>
                                            </div>
                                            {field.isLive ? (
                                                <span className="badge bg-danger bg-opacity-10 text-danger small d-flex align-items-center gap-1 pulse-icon">
                                                    <span className="bg-danger rounded-circle d-inline-block" style={{ width: '6px', height: '6px' }}></span> YAYINDA
                                                </span>
                                            ) : (
                                                <span className="badge bg-light text-muted small border">KAPALI</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {filteredFields.length === 0 && (
                                    <div className="text-center py-5 text-muted">
                                        Filtrelere uygun aktif yayın bulunamadı.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* MAÇ PROGRAMI */}
                <div className="archive-card p-4">
                    <h5 className="fw-bold text-dark mb-1"><CalendarDays className="text-danger" size={18} /> Maç Programı ({scheduleEntries.length})</h5>
                    <p className="text-muted small mb-4">Hangi sahada, hangi tarih ve saatte maç var, önceden gör; canlı yayına zamanında bağlan.</p>

                    <div className="row g-3">
                        {scheduleEntries.length > 0 ? scheduleEntries.map(entry => (
                            <div key={entry.id} className="col-md-6 col-lg-4">
                                <div className="archive-item border p-3 h-100">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="fw-bold text-dark mb-0">{entry.fieldName}</h6>
                                        {entry.isLive ? (
                                            <span className="badge bg-danger d-flex align-items-center gap-1 pulse-icon" style={{ fontSize: '9px' }}>
                                                <span className="bg-white rounded-circle d-inline-block" style={{ width: '5px', height: '5px' }}></span> CANLI
                                            </span>
                                        ) : (
                                            <span className="badge bg-light text-muted border" style={{ fontSize: '9px' }}>KAMERA YOK</span>
                                        )}
                                    </div>
                                    <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                                        <MapPin size={12} /> {entry.city} / {entry.district}
                                    </div>
                                    <div className="text-muted small d-flex align-items-center gap-1 mb-1">
                                        <Calendar size={12} /> {formatDateLabel(entry.date)}
                                    </div>
                                    <div className="text-dark small d-flex align-items-center gap-1 fw-bold">
                                        <Clock size={12} /> {entry.time || "Saat belirtilmemiş"}
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="col-12 text-center py-5 text-muted">
                                Filtrelere uygun planlanmış maç bulunamadı.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveStream;
